// История проверок одного рейса: что и когда находили, у кого, почём.
//
// История живёт на сервере, а не в состоянии поездки, — почему именно так,
// написано в README модуля. Здесь важно другое: цену блока меняет ИМЕННО этот
// модал, по явному нажатию «Поставить эту цену». Само по себе ничего не
// подставляется, и никакая проверка цену не трогает.
import { useCallback, useEffect, useId, useState, type ReactElement } from 'react';
import type { FlightSegment } from '../types';
import { formatMoney } from '../core';
import { useT } from '../i18n';
import { isSegmentLocked, useActions, useStore } from '../store';
import { Modal } from '../ui/shell/Modal';
import { fetchPriceHistory } from './api';
import { usePrices, usePricesTripId } from './PricesProvider';
import { formatStamp, historyValues, sparklinePoints } from './staleness';
import type { PriceCheck, PriceQuote } from './types';
import styles from './PriceHistoryModal.module.css';

export interface PriceHistoryModalProps {
  /** null — модал закрыт. */
  segmentId: string | null;
  onClose(): void;
}

/** Размеры спарклайна в единицах viewBox: сам он тянется по ширине панели. */
const SPARK_W = 320;
const SPARK_H = 44;

export function PriceHistoryModal({
  segmentId,
  onClose,
}: PriceHistoryModalProps): ReactElement | null {
  const { ready, applyQuote, errorFor } = usePrices();
  const tripId = usePricesTripId();
  const state = useStore();
  const actions = useActions();
  const t = useT();

  const [rows, setRows] = useState<PriceCheck[]>([]);
  const [loading, setLoading] = useState(false);

  // Своя метка, как и у кнопки: неудачу записи в историю показывает тот, кто
  // её затеял. Без этого сообщение осталось бы вообще без адресата и человек
  // решил бы, что отметка сохранилась.
  const ownerId = useId();
  const applyError = errorFor(ownerId);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (segmentId === null) return;
      setLoading(true);
      const res = await fetchPriceHistory(segmentId, tripId, signal);
      if (signal?.aborted) return;
      setLoading(false);
      setRows(res.ok ? res.value.checks : []);
    },
    [segmentId, tripId],
  );

  useEffect(() => {
    if (segmentId === null) return;
    const ctrl = new AbortController();
    setRows([]);
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [segmentId, load]);

  if (!ready || segmentId === null) return null;

  const raw = state.library[segmentId];
  const segment: FlightSegment | null = raw && raw.kind === 'flight' ? raw : null;
  // Замок варианта закрывает и правку его блоков из библиотеки — цену через
  // историю он обязан закрывать тем же правилом, а не своим.
  const locked = isSegmentLocked(state, segmentId);
  const canApply = segment !== null && !locked;

  const values = historyValues(rows);
  const points = sparklinePoints(values, SPARK_W, SPARK_H);
  const newestId = rows[0]?.id;
  // Все цены истории уже приведены к валюте блока — своя у каждой строки быть
  // не может, поэтому валюта одна на весь модал.
  const currency = rows[0]?.baseCurrency ?? segment?.price.currency ?? state.displayCurrency;

  async function handleApply(quote: PriceQuote): Promise<void> {
    if (segment === null) return;
    // Порядок важен: сначала запись в историю — ей нужна ЦЕНА ДО замены,
    // и только потом сама замена. Ошибку записи покажет провайдер, но цену
    // всё равно ставим: об этом человека и просили нажатием.
    await applyQuote(segment, quote, ownerId);
    actions.updateSegment(segment.id, {
      price: { amount: quote.normMinor, currency: segment.price.currency },
    });
    await load();
  }

  return (
    <Modal title={t('prices.history.title')} onClose={onClose} wide>
      <div className={styles.body} aria-busy={loading || undefined}>
        {/* Цену в блок мы поставили — её человек и просил, — а вот записать
            это в историю не вышло. Молчать нельзя: с виду всё получилось. */}
        <p role="status" aria-live="polite" className={applyError ? styles.error : 'visually-hidden'}>
          {applyError ?? ''}
        </p>

        {points !== '' && (
          <div className={styles.sparkWrap}>
            <svg
              className={styles.spark}
              viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <polyline
                points={points}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            {/* Сам по себе график — только форма; числа под ним говорят,
                между какими ценами она нарисована. */}
            <div className={styles.sparkScale}>
              <span className="mono">
                {formatMoney(Math.min(...values), currency, state.currencies)}
              </span>
              <span className="mono">
                {formatMoney(Math.max(...values), currency, state.currencies)}
              </span>
            </div>
          </div>
        )}

        {rows.length === 0 ? (
          !loading && <p className={styles.empty}>{t('prices.history.empty')}</p>
        ) : (
          <ul className={styles.rows}>
            {rows.map((row) => (
              <li key={row.id} className={styles.row}>
                <div className={styles.head}>
                  {/* Штамп у проверки уже внутри подписи (`prices.history.check`
                      = «Проверка {when}»), у отметки «поставлена» его нет —
                      дорисовываем рядом, чтобы у всех строк была дата. */}
                  <span className={styles.kind}>
                    {t(row.kind === 'apply' ? 'prices.history.applied' : 'prices.history.check', {
                      when: formatStamp(row.checkedAt),
                    })}
                  </span>
                  {row.kind === 'apply' && (
                    <span className={`mono ${styles.stamp}`}>{formatStamp(row.checkedAt)}</span>
                  )}
                  <span className={`mono ${styles.best}`}>
                    {row.bestMinor === null
                      ? '—'
                      : formatMoney(row.bestMinor, currency, state.currencies)}
                  </span>
                  {row.bestSite !== '' && <span className={styles.site}>{row.bestSite}</span>}
                </div>

                {row.status === 'not_found' && <p className={styles.note}>{t('prices.notfound')}</p>}
                {row.status === 'error' && (
                  <p className={styles.note}>{row.note || t('prices.err.unknown')}</p>
                )}

                {row.quotes.length > 0 && (
                  <details className={styles.quotes} open={row.id === newestId}>
                    <summary className={styles.summary}>
                      {t('prices.history.quotes', { count: row.quotes.length })}
                    </summary>
                    <ul className={styles.quoteList}>
                      {row.quotes.map((q, i) => (
                        <li key={`${row.id}-${i}`} className={styles.quote}>
                          {q.url === '' ? (
                            <span className={styles.qSite}>{q.site}</span>
                          ) : (
                            <a
                              className={styles.qSite}
                              href={q.url}
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              {q.site}
                            </a>
                          )}
                          <span className={`mono ${styles.qPrice}`}>
                            {formatMoney(q.normMinor, currency, state.currencies)}
                          </span>
                          {q.exact && <span className={styles.exact}>{t('prices.history.exact')}</span>}
                          {q.note !== '' && <span className={styles.qNote}>{q.note}</span>}
                          {/* Ставить можно только из свежей проверки: цена из
                              позапрошлой недели — уже не цена, а история. */}
                          {canApply && row.id === newestId && row.kind === 'check' && (
                            <button
                              type="button"
                              className={`btn btn-sm ${styles.applyBtn}`}
                              onClick={() => void handleApply(q)}
                            >
                              {t('prices.action.apply')}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
